// Schedule › Book a visit, Blueprint only (pages/ux-full schedule/book; BL-189): its own skeleton
// (patterns/README), the title block (PageHead) over two columns, Who and where
// with What under it, then When and who, each a Section, drawn only with catalogued parts. What it does
// is use-book-form.ts's, the same as Book.tsx's form (on a call, and Riverstone's Book a job).
import { useLayoutEffect, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Choice,
  ChoiceGroup,
  Link,
  MatchList,
  NewCustomerFields,
  Segmented,
  SelectField,
  TextField,
  WindowWeek,
} from '@flowology/ui-web/fl';
import { PageHead, Section } from '@flowology/ui-web/patterns';
import {
  confirmationText,
  dayLabel,
  draftHasInput,
  dropTag,
  fromCallWords,
  hoursWords,
  lastBookDay,
  pickTag,
  summaryFromNotes,
  tagOptions,
  tagTo,
  todayPassed,
  WEEK_PAGES,
  weekPageOf,
  windowLabel,
  windowsFor,
  type BookTag,
  type WindowCap,
} from '../book-model.ts';
import { openJob } from '../job/job-page.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { MIRROR_LINE } from '../shared/mirror.ts';
import { formFromSearch, type CustomerForm } from '../shared/new-customer-model.ts';
import { termsAt, termsText } from '../shared/terms.ts';
import { useNewCustomer } from '../shared/use-new-customer.ts';
import type { visitTextTo } from '../types.ts';
import { phoneText, type BookStart, type Customer } from '../types.ts';
import { soonestWords, weekNote, weekWords } from './book-page-model.ts';
import {
  useAddressNotes,
  useBookForm,
  useCapacityAdjust,
  useTagList,
  type BookFormState,
} from './use-book-form.ts';
import './book-page.css';

export const BOOK_LEDE =
  'Pick who it’s for, say what’s wrong, then pick when. It goes straight onto the day board.';

export function BookPage({
  nav,
  start,
  timeZone,
  onBoard,
}: {
  nav: NavProps;
  start: BookStart | null;
  timeZone: string;
  /** To the board; from a booking, on its day with the visit open (QA-768). */
  onBoard: (at?: { date: string; visit: string }) => void;
}) {
  const crumb = usePageCrumb(nav);
  const f = useBookForm({ nav, start, timeZone });
  return (
    <div className="app">
      <main className="bp-book-main">
        <PageBanners nav={nav} />
        <PageHead {...(crumb ? { crumb } : {})} title="Book a visit" lede={BOOK_LEDE} />
        <div className="bp-book">
          {f.mirroring ? (
            <Section title="Book a visit">
              <p className="bp-book-line">{MIRROR_LINE}</p>
              <div className="bp-book-acts">
                <Button onPress={() => onBoard()}>See the dispatch board</Button>
              </div>
            </Section>
          ) : f.booked ? (
            <BookedNow f={f} onBoard={onBoard} />
          ) : (
            <BookFields f={f} start={start} />
          )}
        </div>
      </main>
    </div>
  );
}

/** What was just booked, and where to go next. */
function BookedNow({
  f,
  onBoard,
}: {
  f: BookFormState;
  onBoard: (at?: { date: string; visit: string }) => void;
}) {
  const booked = f.booked!;
  const { d, estimate, textTo } = f;
  return (
    <>
      <p className="bp-book-said" role="status">
        <b>Booked #{booked.jobNumber}.</b> It’s on the dispatch board
        {estimate ? `, with the work sold on estimate #${estimate.number}` : ''}
        {booked.texted ? ', and the confirmation text is on its way' : ''}.
      </p>
      <Section title={`#${booked.jobNumber} · ${f.types.find((t) => t.id === d.jobTypeId)?.name ?? ''}`}>
        <div className="bp-book-stack">
          <p className="bp-book-line" data-muted="">
            {f.customer?.displayName} · {f.dayLabel}, arriving {d.window && windowLabel(d.window)}
          </p>
          <p className="bp-book-line">{d.summary}</p>
          {!booked.texted && (
            <p className="bp-book-line" data-small="">
              {f.textingOff
                ? 'Not sent: texting isn’t set up for your company yet.'
                : `No confirmation text${textTo && 'reason' in textTo ? `: ${textTo.reason}` : ''}.`}
            </p>
          )}
          <div className="bp-book-acts">
            {/* on its day, the visit open and in view, not the board where it was last left (QA-768) */}
            <Button variant="primary" onPress={() => onBoard({ date: d.date, visit: booked.appointmentId })}>
              See it on the dispatch board
            </Button>
            <Button onPress={f.bookAnother}>Book another</Button>
          </div>
        </div>
      </Section>
    </>
  );
}

function BookFields({ f, start }: { f: BookFormState; start: BookStart | null }) {
  const { d, patch, customer, estimate, bad } = f;
  // the field the last refusal named, marked until it is changed (QA-720); the why is under Book
  const invalid = (k: Parameters<BookFormState['invalid']>[0]) =>
    bad === k ? { isInvalid: true, describedBy: 'book-refused' } : {};
  const bookWords = `Book ${d.kind === 'estimate_visit' ? 'estimate visit' : 'service job'}`;
  // Book's words measured while idle, so the busy ones keep its width
  const hold = useRef<HTMLSpanElement>(null);
  const [holdW, setHoldW] = useState(0);
  useLayoutEffect(() => {
    if (!f.busy && hold.current) setHoldW(hold.current.getBoundingClientRect().width);
  }, [f.busy, bookWords]);
  const fromCall = fromCallWords(!!start?.locationId, !!summaryFromNotes(start?.notes));
  const [jumpError, setJumpError] = useState<string | null>(null);
  const today = f.today;
  const last = lastBookDay(today);
  const length = (f.readOf(f.page) ?? f.readOf(0))?.jobType?.durationMinutes ?? null;
  const week = weekWords(f.grids[f.page], today, { date: d.date, window: d.window });
  return (
    <>
      {f.restored && f.plain && draftHasInput(d, f.query) && (
        <div className="bp-book-said" role="status">
          <span>Your unsent booking is kept from before; nothing is booked yet.</span>{' '}
          <Link onPress={f.startOver}>Start over</Link>
        </div>
      )}
      {estimate && (
        <p className="bp-book-said">
          Booking the work sold on estimate #{estimate.number}. Its lines go on the new job’s invoice, at the
          address it was sold for, and it leaves To book.
        </p>
      )}
      {start?.lead && d.leadId === start.lead.id && (
        <p className="bp-book-said">
          Booking the lead from{' '}
          {start.lead.name ?? (start.lead.phone ? phoneText(start.lead.phone) : 'this request')}. Once it’s
          booked, the lead leaves To book.
        </p>
      )}
      {d.callId && (
        <p className="bp-book-said">
          Linked to the call. The job keeps it, so its timeline starts with the call.
          {fromCall && ` ${fromCall}`}
        </p>
      )}
      <div className="bp-book-cols">
        <Section title="Who and where">
          <div className="bp-book-stack">
            <TextField
              label="Customer (type a name, number or street)"
              value={f.query}
              onChange={f.typeCustomer}
              inputRef={f.customerField}
              autoComplete="off"
              isReadOnly={!!estimate}
              combobox={{ expanded: f.found.length > 0, controls: 'cust-found' }}
              {...invalid('customer')}
            />
            {f.found.length > 0 && !f.newCustomer && (
              <MatchList
                id="cust-found"
                label="Customers found"
                items={f.found.map((c) => ({ id: c.id, name: c.displayName, detail: c.city }))}
                onPick={(id) => void f.pickCustomer(id)}
              />
            )}
            {!customer && !estimate && !f.newCustomer && (
              <div className="bp-book-acts">
                <Button onPress={() => f.setNewCustomer(formFromSearch(f.query))}>New customer</Button>
              </div>
            )}
            {f.newCustomer && (
              <div className="bp-book-stack" role="group" aria-label="New customer">
                <h3 className="bp-book-sub">New customer</h3>
                <AddCustomer
                  start={f.newCustomer}
                  onDone={(id) => {
                    f.setNewCustomer(null);
                    void f.pickCustomer(id).then(() => f.customerField.current?.focus());
                  }}
                  onCancel={() => {
                    f.setNewCustomer(null);
                    f.customerField.current?.focus();
                  }}
                />
              </div>
            )}
            {customer && f.hold && (
              <p className="bp-book-said" data-warn="" role="status">
                <b>On a collections hold</b>
                {f.hold.reason ? `: ${f.hold.reason}` : ''}. Emergency work goes ahead; check with whoever
                keeps the books before booking anything else.
              </p>
            )}
            {customer && estimate && (
              <>
                <p className="bp-book-line" data-small="">
                  Address: <b>{f.soldAt ?? 'Loading…'}</b>{' '}
                  <span className="bp-book-muted">(where the estimate was sold)</span>
                </p>
                <BillsTo customer={customer} locationId={d.locationId} />
                {f.textTo?.siteContact && <TextsGoTo to={f.textTo} />}
                <AddressNotes locationId={d.locationId} />
              </>
            )}
            {customer && !estimate && <Where f={f} customer={customer} invalid={invalid} />}
            <h3 className="bp-book-sub">What</h3>
            <div className="bp-book-pair">
              {/* a company with one department has nothing to choose here (QA-346) */}
              {f.units.length > 1 && (
                <SelectField
                  label="Department"
                  value={d.businessUnitId ?? ''}
                  onChange={(v) => patch({ businessUnitId: v, jobTypeId: null })}
                  options={f.units.map((u) => ({ value: u.id, label: u.name }))}
                  {...invalid('department')}
                />
              )}
              <SelectField
                label="Job type"
                value={d.jobTypeId ?? ''}
                onChange={(v) => patch({ jobTypeId: v || null })}
                options={[
                  { value: '', label: f.typesRead && !f.types.length ? 'None yet' : 'Pick one' },
                  ...f.unitTypes.map((t) => ({ value: t.id, label: t.name })),
                ]}
                {...(f.typesRead && !f.types.length ? { describedBy: 'book-no-types' } : {})}
                {...invalid('jobType')}
              />
            </div>
            {/* QA-723: with none, say so and where they're added; what's filled in here is kept */}
            {f.typesRead && !f.types.length && (
              <p className="bp-book-said" data-warn="" id="book-no-types">
                No job types yet.{' '}
                {f.mayAddTypes ? (
                  <>
                    Add one in <Link href="#/settings/org">Setup › Job types</Link>, then come back: what
                    you’ve filled in here is kept.
                  </>
                ) : (
                  'The owner or a manager adds them in Setup › Job types.'
                )}
              </p>
            )}
            <TextField
              label="Summary (required: the tech reads this first)"
              multiline
              value={d.summary}
              placeholder="What is wrong, where, anything the tech should bring"
              onChange={(v) => patch({ summary: v })}
              {...invalid('summary')}
            />
            {f.wet && <Water f={f} />}
            <div className="bp-book-choose">
              <span className="bp-book-label">Visit kind</span>
              <Segmented
                label="Visit kind"
                items={[
                  { id: 'estimate_visit', label: 'Estimate visit (default)', isDisabled: !!estimate },
                  { id: 'service', label: 'Service (known fix)' },
                ]}
                selectedKey={d.kind}
                onSelectionChange={(k) => patch({ kind: k as typeof d.kind })}
              />
            </div>
            {/* an estimate's work is booked by estimates.bookSold, which takes no tags */}
            {!estimate && <BookTags customer={customer} tags={d.tags} onChange={(tags) => patch({ tags })} />}
          </div>
        </Section>

        <Section title="When and who">
          <div className="bp-book-stack">
            <Checkbox
              isSelected={d.afterHours}
              onChange={(on) => {
                patch({
                  afterHours: on,
                  window: null,
                  ...(on ? { date: today } : d.date === today ? { date: f.opens } : {}),
                });
                f.setPage(0);
              }}
            >
              After hours or emergency
            </Checkbox>
            {d.afterHours && (
              <TextField
                label="Why it can’t wait (required)"
                value={d.afterHoursReason}
                placeholder="e.g. active leak, no water, no heat"
                onChange={(v) => patch({ afterHoursReason: v })}
                {...invalid('afterHours')}
              />
            )}
            {d.afterHours ? (
              <>
                {/* after hours: the evening's windows for a day, today first (no capacity is read for them) */}
                <div className="bp-book-jump">
                  <TextField
                    label="Day"
                    type="date"
                    min={today}
                    max={last}
                    value={d.date}
                    onChange={(v) => v && weekPageOf(today, v) !== null && patch({ date: v, window: null })}
                  />
                </div>
                {/* a ChoiceGroup wraps on a phone, where four segments would run off the page */}
                <ChoiceGroup
                  selectionMode="single"
                  label="Arrival window"
                  value={d.window ? String(d.window[0]) : ''}
                  onChange={(k) => {
                    const w = windowsFor(true, d.date, today, f.nowH).find((x) => String(x.window[0]) === k);
                    if (w) patch({ window: w.window });
                  }}
                >
                  {windowsFor(true, d.date, today, f.nowH).map(({ window: w, label, started }) => (
                    <Choice key={w[0]} value={String(w[0])} isDisabled={started}>
                      {label}
                    </Choice>
                  ))}
                </ChoiceGroup>
              </>
            ) : (
              <div className="bp-book-choose" data-wide="">
                <span className="bp-book-label">
                  Arrival window{bad === 'window' && <span className="bp-book-bad"> · check it</span>}
                </span>
                <WindowWeek
                  soonest={soonestWords(f.soon, length, today)}
                  onSoonest={() => 'found' in f.soon && f.pickCell(f.soon.found)}
                  range={week.range}
                  {...(f.page > 0 ? { onEarlier: () => f.setPage(f.page - 1) } : {})}
                  {...(f.page < WEEK_PAGES - 1 ? { onLater: () => f.setPage(f.page + 1) } : {})}
                  days={week.days}
                  rows={week.rows}
                  failed={
                    f.readOf(f.page) === null
                      ? 'Couldn’t get the open times for this week. You can still pick a window.'
                      : null
                  }
                  note={weekNote(length)}
                  onPick={(row, col) => {
                    const c = f.grids[f.page]?.rows[row]?.cells[col];
                    if (c) f.pickCell(c);
                  }}
                  jump={{
                    value: d.date,
                    min: today,
                    max: last,
                    error: jumpError,
                    onChange: (v) => {
                      if (weekPageOf(today, v) === null)
                        return setJumpError(`Pick a day from today to ${dayLabel(last, today)}.`);
                      setJumpError(null);
                      patch({ date: v, window: null });
                      f.setPage(weekPageOf(today, v) ?? 0);
                    },
                  }}
                />
                {todayPassed(f.nowH) && (
                  <p className="bp-book-line" data-small="">
                    Today’s windows have passed. For tonight, tick After hours or emergency.
                  </p>
                )}
              </div>
            )}
            {f.capWarn && (
              <p className="bp-book-said" data-warn="">
                {f.capWarn}
              </p>
            )}
            {f.mayAdjust && f.chosenCap && d.window && (
              <CapacityAdjust
                key={`${d.date}:${d.window[0]}`}
                date={d.date}
                windowStartHour={d.window[0]}
                window={f.chosenCap}
                businessUnitId={d.businessUnitId}
                onChanged={() => f.setCapTry((n) => n + 1)}
              />
            )}
            <ChoiceGroup
              label="Technicians (a suggestion; the dispatcher can move it)"
              value={d.techIds}
              onChange={(ids) => patch({ techIds: ids })}
            >
              {f.techs.map((t) => (
                <Choice
                  key={t.id}
                  value={t.id}
                  // free in the chosen window (BL-057)
                  {...(f.chosenCap?.freeTechIds.includes(t.id) ? { description: 'free then' } : {})}
                >
                  {t.name}
                </Choice>
              ))}
            </ChoiceGroup>
            {!d.techIds.length && (
              <p className="bp-book-line" data-small="">
                No tech yet: it waits on the dispatch board.
              </p>
            )}
            {f.clashes.length > 0 && (
              <p className="bp-book-said" data-warn="">
                Overlap: {f.clashes.map((c) => `${c.techName} already has #${c.jobNumber}`).join('; ')}. You
                can still book; the dispatcher will see both.
              </p>
            )}
            {customer && <Confirmation f={f} />}
            <div className="bp-book-acts" data-col="">
              <Button
                variant="primary"
                isBusy={f.busy}
                isDisabled={f.stops.length > 0 || !!f.dupe}
                onPress={() => void f.book()}
              >
                {/* as wide as its idle words, so "Booking…" never shrinks it under the tap */}
                <span
                  ref={hold}
                  className="bp-book-hold"
                  style={f.busy && holdW ? { minInlineSize: holdW } : undefined}
                >
                  {f.busy ? 'Booking…' : bookWords}
                </span>
              </Button>
              {f.error && !f.busy && (
                <p className="bp-book-error" role="alert" id="book-refused">
                  {f.error}
                </p>
              )}
            </div>
            {f.dupe && (
              <div className="bp-book-said" data-warn="" role="alert">
                <p className="bp-book-line">{f.dupe.message}</p>
                <div className="bp-book-acts">
                  <Button isDisabled={f.busy} onPress={() => void f.book(true)}>
                    Book anyway
                  </Button>
                  <Link onPress={() => openJob(f.dupe!.jobId)}>Open #{f.dupe.jobNumber}</Link>
                </div>
              </div>
            )}
            {f.stops.length > 0 && (
              <p className="bp-book-line" data-small="">
                To book: {f.stops.join(', ')}.
              </p>
            )}
          </div>
        </Section>
      </div>
    </>
  );
}

/** A customer who isn't on file yet, added right here (QA-323), started from what was typed. */
function AddCustomer({
  start,
  onDone,
  onCancel,
}: {
  start: CustomerForm;
  onDone: (id: string) => void;
  onCancel: () => void;
}) {
  const nc = useNewCustomer({ start, pick: 'Use', onDone });
  return (
    <NewCustomerFields
      values={nc.form}
      onChange={nc.change}
      onCheck={nc.check}
      consentNote="Consent (saved with who and when)"
      submitLabel="Add customer"
      busyLabel="Adding the customer…"
      isBusy={nc.busy}
      isSubmitDisabled={nc.blocked}
      error={nc.error}
      onSubmit={() => nc.create()}
      onCancel={onCancel}
      dupe={
        nc.dupe
          ? {
              text: nc.dupe.text,
              pickLabel: nc.dupe.pickLabel,
              onPick: nc.dupe.pick,
              isHeld: nc.held !== null,
              onAddAnyway: () => nc.create(true),
            }
          : null
      }
      unchecked={nc.uncheckedText ? { text: nc.uncheckedText, onAddAnyway: () => nc.create(true) } : null}
    />
  );
}

/** The customer's address, its bill-to, who hears about the visit, its notes, their PO and a new address. */
function Where({
  f,
  customer,
  invalid,
}: {
  f: BookFormState;
  customer: Customer;
  invalid: (k: Parameters<BookFormState['invalid']>[0]) => { isInvalid?: boolean; describedBy?: string };
}) {
  const { d, patch, newLoc, setNewLoc } = f;
  // The customer's PO, when they give one now (F-29): never required to book; Review asks.
  const t = termsAt(customer, d.locationId);
  return (
    <>
      <SelectField
        label="Location"
        value={d.locationId ?? ''}
        onChange={(v) => patch({ locationId: v })}
        options={customer.locations.map((l) => ({ value: l.id, label: `${l.line1}, ${l.city}` }))}
        {...invalid('location')}
      />
      <BillsTo customer={customer} locationId={d.locationId} />
      {f.textTo?.siteContact && <TextsGoTo to={f.textTo} />}
      <AddressNotes locationId={d.locationId} />
      <TextField
        label={t.terms?.poRequired ? `Customer’s PO number (${t.name} requires one)` : 'Customer’s PO number'}
        value={d.poNumber}
        maxLength={60}
        onChange={(v) => patch({ poNumber: v })}
        placeholder={t.terms?.poRequired ? 'or add it before the job is reviewed' : 'optional'}
        autoComplete="off"
        {...invalid('po')}
      />
      {newLoc ? (
        <div className="bp-book-stack">
          <div className="bp-book-pair">
            <TextField
              label="Street"
              value={newLoc.line1}
              onChange={(v) => setNewLoc({ ...newLoc, line1: v })}
            />
            <TextField label="City" value={newLoc.city} onChange={(v) => setNewLoc({ ...newLoc, city: v })} />
          </div>
          <div className="bp-book-acts">
            <Button
              isBusy={f.adding}
              isDisabled={!newLoc.line1.trim() || !newLoc.city.trim()}
              onPress={() => void f.addLocation()}
            >
              {f.adding ? 'Adding…' : 'Add this location'}
            </Button>
            <Link onPress={() => setNewLoc(null)}>Cancel</Link>
          </div>
          {f.addError && !f.adding && (
            <p className="bp-book-error" role="alert">
              {f.addError}
            </p>
          )}
        </div>
      ) : (
        <div className="bp-book-acts">
          <Link onPress={() => setNewLoc({ line1: '', city: '' })}>Add location</Link>
        </div>
      )}
    </>
  );
}

/**
 * Who the invoice goes to (F-54, QA-253), a property manager or strata or the customer, and their
 * terms (F-29): "Bills to: Harbourview Strata (for Okafor family) · Net 30 · PO required".
 */
function BillsTo({ customer, locationId }: { customer: Customer; locationId: string | null }) {
  const t = termsAt(customer, locationId);
  const words = termsText(t.terms);
  return (
    <p className="bp-book-line" data-small="">
      Bills to: <b>{t.name}</b>
      {t.billTo && <span className="bp-book-muted"> (for {customer.displayName})</span>}
      {words && <span> · {words}</span>}
    </p>
  );
}

/**
 * Who hears about this visit (F-54), when the address has a site contact: "Texts go to Dee Alvarez
 * (site contact)", or why none will go. Never the customer instead.
 */
function TextsGoTo({ to }: { to: NonNullable<ReturnType<typeof visitTextTo>> }) {
  return 'method' in to ? (
    <p className="bp-book-line" data-small="">
      Texts go to <b>{to.siteContact}</b> <span className="bp-book-muted">(site contact)</span>
    </p>
  ) : (
    <p className="bp-book-line" data-small="">
      <span className="bp-book-word" data-bad="">
        No visit texts
      </span>{' '}
      {to.reason[0]!.toUpperCase() + to.reason.slice(1)}.
    </p>
  );
}

/** The chosen address's alerts and warranties (records.forLocation): neither stops the booking. */
function AddressNotes({ locationId }: { locationId: string | null }) {
  const { alerts, warranties } = useAddressNotes(locationId);
  if (!alerts.length && !warranties.length) return null;
  return (
    <div className="bp-book-stack" aria-label="About this address">
      {alerts.map((a) => (
        <p key={a.id} className="bp-book-said" data-warn="" data-small="">
          <b>Alert:</b> {a.body}
        </p>
      ))}
      {warranties.map((w) => (
        <p key={w} className="bp-book-said" data-small="">
          {w}
        </p>
      ))}
    </div>
  );
}

/** A leak or a burst: is water running now, and is it shut off? It decides how soon. */
function Water({ f }: { f: BookFormState }) {
  const { water, answerWater } = f;
  const yesNo = [
    { id: 'yes', label: 'Yes' },
    { id: 'no', label: 'No' },
  ];
  const said = (v: boolean | null) => (v === true ? 'yes' : v === false ? 'no' : '');
  return (
    <div className="bp-book-stack" role="group" aria-label="Water">
      <div className="bp-book-choose">
        <span className="bp-book-label">Is water running now?</span>
        <Segmented
          label="Is water running now?"
          items={yesNo}
          selectedKey={said(water.running)}
          onSelectionChange={(k) =>
            answerWater(
              k === 'yes' ? { running: true, shutOff: water.shutOff } : { running: false, shutOff: null },
            )
          }
        />
      </div>
      {water.running && (
        <div className="bp-book-choose">
          <span className="bp-book-label">Is it shut off?</span>
          <Segmented
            label="Is it shut off?"
            items={yesNo}
            selectedKey={said(water.shutOff)}
            onSelectionChange={(k) => answerWater({ running: true, shutOff: k === 'yes' })}
          />
        </div>
      )}
      {f.waterSaid && (
        <p className="bp-book-said" data-small="" role="status">
          {f.waterSaid}
        </p>
      )}
    </div>
  );
}

/** The confirmation text to the customer (or the site contact), or why none will go. */
function Confirmation({ f }: { f: BookFormState }) {
  const { d, textTo, patch } = f;
  if (!(textTo && 'method' in textTo))
    return (
      <p className="bp-book-line" data-small="">
        No confirmation text: {textTo && 'reason' in textTo ? textTo.reason : ''}.
      </p>
    );
  return (
    <>
      <Checkbox isSelected={d.sendConfirmation} onChange={(on) => patch({ sendConfirmation: on })}>
        Text {textTo.firstName} a confirmation
      </Checkbox>
      {d.sendConfirmation && d.window && (
        <p className="bp-book-sms">
          {confirmationText({
            firstName: textTo.firstName,
            company: f.signedBy,
            dayLabel: f.dayLabel,
            window: d.window,
          })}
        </p>
      )}
    </>
  );
}

/**
 * The office's own change to the chosen window (BL-057 phase 2), for those who dispatch: close it for
 * the day, or add on-call time, with a reason; or take back a change made.
 */
function CapacityAdjust(p: {
  date: string;
  windowStartHour: number;
  window: WindowCap;
  businessUnitId: string | null;
  onChanged: () => void;
}) {
  const c = useCapacityAdjust(p);
  return (
    <div className="bp-book-stack" data-tight="" role="group" aria-label={`Capacity of ${p.window.label}`}>
      {c.made.map((a) => (
        <p key={a.id} className="bp-book-line" data-small="">
          <span>
            {a.closed
              ? 'Closed'
              : a.minutes > 0
                ? `${hoursWords(a.minutes)} added`
                : `${hoursWords(-a.minutes)} taken off`}
            : {a.reason}
          </span>{' '}
          <Link isDisabled={c.busy} onPress={() => c.undo(a.id)}>
            Undo
          </Link>
        </p>
      ))}
      {!c.open ? (
        <div className="bp-book-acts">
          <Link onPress={() => c.setOpen(true)}>Change {p.window.label}’s capacity</Link>
        </div>
      ) : (
        <div className="bp-book-stack">
          <Segmented
            label="Change"
            items={[
              { id: 'close', label: 'Close it for the day' },
              { id: 'add', label: 'Add on-call time' },
            ]}
            selectedKey={c.kind}
            onSelectionChange={(k) => c.setKind(k as 'close' | 'add')}
          />
          {c.kind === 'add' && (
            <SelectField
              label="How long"
              value={String(c.minutes)}
              onChange={(v) => c.setMinutes(Number(v))}
              options={[30, 60, 90, 120].map((m) => ({ value: String(m), label: hoursWords(m) }))}
            />
          )}
          <TextField label="Why" value={c.reason} maxLength={500} onChange={c.setReason} />
          {c.error && (
            <p className="bp-book-error" role="alert">
              {c.error}
            </p>
          )}
          <div className="bp-book-acts">
            <Button isDisabled={c.busy || !c.reason.trim()} onPress={c.apply}>
              {c.kind === 'close' ? 'Close it' : 'Add it'}
            </Button>
            <Link onPress={() => c.setOpen(false)}>Cancel</Link>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * BL-129: tags when booking. Each picked tag is saved to this job, and, ticked, to the customer too, as
 * their page puts one on (jobs.book's customerTagIds: refused to someone who can't edit customers).
 */
function BookTags({
  customer,
  tags,
  onChange,
}: {
  customer: Customer | null;
  tags: BookTag[];
  onChange: (tags: BookTag[]) => void;
}) {
  const { all, failed } = useTagList();
  const offered = all ? tagOptions(all, tags) : [];
  const has = customer?.tags ?? [];
  return (
    <>
      <SelectField
        label="Tags"
        value=""
        isDisabled={!all || !offered.length}
        onChange={(v) => {
          const t = offered.find((x) => x.id === v);
          if (t) onChange(pickTag(tags, t));
        }}
        options={[
          {
            value: '',
            label: failed
              ? 'Couldn’t get the tags'
              : !all
                ? 'Getting the tags…'
                : offered.length
                  ? 'Add a tag'
                  : 'Every tag is picked',
          },
          ...offered.map((t) => ({ value: t.id, label: t.name })),
        ]}
      />
      {customer && has.length > 0 && (
        <p className="bp-book-line" data-small="">
          <span className="bp-book-muted">{customer.displayName} has</span>
          {has.map((t) => (
            <span key={t.id} className="bp-book-word">
              {t.name}
            </span>
          ))}
        </p>
      )}
      {tags.map((t) => (
        <div key={t.id} className="bp-book-tag" role="group" aria-label={`Tag ${t.name}`}>
          <span className="bp-book-word">{t.name}</span>
          <Checkbox isSelected={t.job} onChange={(on) => onChange(tagTo(tags, t.id, 'job', on))}>
            Save to this job
          </Checkbox>
          <Checkbox isSelected={t.customer} onChange={(on) => onChange(tagTo(tags, t.id, 'customer', on))}>
            Save to the customer
          </Checkbox>
          <Link onPress={() => onChange(dropTag(tags, t.id))}>Remove</Link>
        </div>
      ))}
    </>
  );
}
